import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useI18n } from "../lib/i18n";

const TYPES = ["image", "video", "scene", "sequence", "short_film", "commercial", "music_video", "trailer", "movie"];

export default function Projects() {
  const { locale } = useI18n();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", type: "short_film", target_duration: 300, aspect_ratio: "16:9", visual_style: "cinematic" });
  const [open, setOpen] = useState(false);

  const load = () => api.get("/projects").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/projects", { ...form, target_duration: Number(form.target_duration), language: locale });
      setOpen(false);
      setForm({ ...form, title: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => {
    await api.delete(`/projects/${id}`).catch((e) => toast.error(apiErr(e)));
    load();
  };

  const sel = "w-full rounded-lg border border-[#212230] bg-[#101118] px-3 py-2.5 text-[13px] outline-none focus:border-[#ff4b2b]/60";

  return (
    <div className="p-6 lg:p-10" data-testid="projects-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Projects</p>
          <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
            {locale === "fr" ? "Vos productions" : "Your productions"}
          </h1>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="new-project-btn" className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold">
          <Plus className="h-4 w-4" /> {locale === "fr" ? "Nouveau projet" : "New project"}
        </button>
      </div>

      {open && (
        <div className="mt-8 grid max-w-[900px] gap-4 rounded-2xl border border-[#212230] bg-[#0b0b11] p-6 sm:grid-cols-2" data-testid="new-project-form">
          <input className={sel} placeholder={locale === "fr" ? "Titre" : "Title"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-title-input" />
          <select className={sel} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="project-type-select">
            {TYPES.map((t) => <option key={t} value={t} children={String(t)} />)}
          </select>
          <select className={sel} value={form.target_duration} onChange={(e) => setForm({ ...form, target_duration: e.target.value })} data-testid="project-duration-select">
            {[15, 30, 60, 120, 300, 600, 900, 1200, 1800].map((d) => (
              <option key={d} value={d} children={d < 60 ? `${d}s` : `${Math.round(d / 60)} min`} />
            ))}
          </select>
          <select className={sel} value={form.aspect_ratio} onChange={(e) => setForm({ ...form, aspect_ratio: e.target.value })}>
            {["16:9", "9:16", "1:1", "4:5", "21:9"].map((r) => <option key={r} value={r} children={String(r)} />)}
          </select>
          <select className={sel} value={form.visual_style} onChange={(e) => setForm({ ...form, visual_style: e.target.value })}>
            {["cinematic", "photorealistic", "animation", "anime", "cartoon", "documentary", "fashion", "sci-fi", "fantasy", "horror", "vintage"].map((s) => <option key={s} value={s} children={String(s)} />)}
          </select>
          <button onClick={create} data-testid="project-create-submit" className="btn-primary rounded-lg py-2.5 text-[13px] font-semibold">
            {locale === "fr" ? "Créer" : "Create"}
          </button>
        </div>
      )}

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((p) => (
          <div key={p.id} className="hot-border relative rounded-xl border border-[#212230] bg-[#0b0b11] p-5" data-testid={`project-card-${p.id}`}>
            <Link to={`/studio/projects/${p.id}`} className="block">
              <p className="mono text-[10px] uppercase tracking-[0.16em] text-[#ff4b2b]">
                {p.is_inbox
                  ? (locale === "fr" ? "Créations du studio" : "Studio creations")
                  : `${p.type} · ${p.target_duration}s · ${p.aspect_ratio}`}
              </p>
              <p className="mt-2 font-display text-base font-medium">{p.title}</p>
              <p className="mt-2 line-clamp-2 text-[12px] text-[#9194a6]">{p.logline || p.description || "—"}</p>
              <p className="mono mt-4 text-[10px] uppercase text-[#8288a0]">{p.status} · {p.version_label}</p>
            </Link>
            <button onClick={() => remove(p.id)} data-testid={`project-delete-${p.id}`} className="absolute right-4 top-4 text-[#8288a0] transition-colors hover:text-[#ff4b4b]">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {list.length === 0 && (
          <p className="text-[13px] text-[#9194a6]">{locale === "fr" ? "Aucun projet pour l'instant." : "No projects yet."}</p>
        )}
      </div>
    </div>
  );
}
